import {Point, Graphics, Container, loader, extras} from 'pixi.js';
import BPromise from 'bluebird';
import {some as _some} from 'lodash/collection';
import {delay as _delay} from 'lodash/function';
import Utils from '../libs/utils';
import Tram from './Tram';
import Bus from './Bus';
import Hud from './Hud';

const MAX_X = 800;
const MAX_Y = 600;

const TRAM_POINTS = {
  ORIGIN: new Point(MAX_X / 2, MAX_Y)
};
const BUS_POINTS = {
  DOWN: new Point(MAX_X / 2, MAX_Y + 100),
  UP: new Point(MAX_X / 2, MAX_Y - 150),
  PRIDE_START: new Point(-100, MAX_Y - 130),
  PRIDE_END: new Point(MAX_X+100, MAX_Y - 130)
};
const HUD_LOCATIONS = {
  SCORE: new Point(MAX_X/2, MAX_Y * 0.85),
  WAVE_STATUS: new Point(MAX_X - 10, MAX_Y - 20),
  GAME_STATUS: new Point(MAX_X / 2, MAX_Y * 0.12),
  PLAY_BUTTON: new Point(MAX_X / 2, MAX_Y * 0.425),
  RANKING_BUTTON: new Point(MAX_X / 2, MAX_Y * 0.562),
  SHARE_BUTTON: new Point(MAX_X / 2 + 125, MAX_Y * 0.32),
  POST_BUTTON: new Point(MAX_X / 2 - 125, MAX_Y * 0.32),
  REPLAY_BUTTON: new Point(MAX_X / 2, MAX_Y * 0.55),
  MUTE_BUTTON: new Point(10, 10),
  FLAG_STATUS: new Point(10, MAX_Y * 0.85),
  SMILE_STATUS: new Point(10, MAX_Y * -0.90),
  SAD_STATUS: new Point(10, MAX_Y * -0.95)
};

const FLASH_MS = 60;
const FLASH_SCREEN = new Graphics();
FLASH_SCREEN.beginFill(0xFFFFFF);
FLASH_SCREEN.drawRect(0, 0, MAX_X, MAX_Y);
FLASH_SCREEN.endFill();
FLASH_SCREEN.position.x = 0;
FLASH_SCREEN.position.y = 0;

class Stage extends Container {

  /**
   * Stage Constructor
   * Container for the game
   * @param opts
   * @param opts.spritesheet - String representing the path to the spritesheet file
   */
  constructor(opts) {
    super();
    this.spritesheet = opts.spritesheet;
    this.interactive = true;
    this.trams = [];
    this.bus = new Bus({
      spritesheet: opts.spritesheet,
      downPoint: BUS_POINTS.DOWN,
      upPoint: BUS_POINTS.UP
    });
    this.bus.visible = false;
    this.flashScreen = FLASH_SCREEN;
    this.flashScreen.visible = false;
    this.mainScreen = new extras.AnimatedSprite([
      loader.resources[this.spritesheet].textures['scene/main/0.png']
    ]);
    this.mainScreen.visible = true;
    this.hud = new Hud();

    this._setStage();
    this.scaleToWindow();
  }

  static scoreBoxLocation() {
    return HUD_LOCATIONS.SCORE;
  }

  static waveStatusBoxLocation() {
    return HUD_LOCATIONS.WAVE_STATUS;
  }

  static gameStatusBoxLocation() {
    return HUD_LOCATIONS.GAME_STATUS;
  }

  static replayButtonLocation() {
    return HUD_LOCATIONS.REPLAY_BUTTON;
  }

  static muteButtonLocation() {
    return HUD_LOCATIONS.MUTE_BUTTON;
  }

  static playButtonLocation() {
    return HUD_LOCATIONS.PLAY_BUTTON;
  }

  static rankingButtonLocation() {
    return HUD_LOCATIONS.RANKING_BUTTON;
  }

  static shareButtonLocation() {
    return HUD_LOCATIONS.SHARE_BUTTON;
  }

  static postButtonLocation() {
    return HUD_LOCATIONS.POST_BUTTON;
  }


  static flagStatusBoxLocation() {
    return HUD_LOCATIONS.FLAG_STATUS;
  }

  static smileStatusBoxLocation() {
    return HUD_LOCATIONS.SMILE_STATUS;
  }

  static sadStatusBoxLocation() {
    return HUD_LOCATIONS.SAD_STATUS;
  }
  /**
   * scaleToWindow
   * Helper method that scales the stage container to the window size
   */
  scaleToWindow() {
    this.scale.set(window.innerWidth / MAX_X, window.innerHeight / MAX_Y);
  }

  /**
   * _setStage
   * Private method that adds all of the main pieces to the scene
   * @returns {Stage}
   * @private
   */
  _setStage() {
    const background = new extras.AnimatedSprite([
      loader.resources[this.spritesheet].textures['scene/back/0.png']
    ]);
    background.position.set(0, 0);

    this.addChild(background);
    this.addChild(this.bus);
    this.addChild(this.hud);
    this.addChild(this.mainScreen);
    this.addChild(this.flashScreen);

    return this;
  }

  /**
   * preLevelAnimation
   * Helper method that runs the level intro animation with the bus and returns a promise that resolves
   * when it's complete.
   * @returns {Promise}
   */
  preLevelAnimation() {
    return new BPromise((resolve) => {
      this.cleanUpTrams();

      const prideOpts = {
        startPoint: BUS_POINTS.PRIDE_START,
        endPoint: BUS_POINTS.PRIDE_END,
        onComplete: () => {
          resolve();
        }
      };

      this.bus.pride(prideOpts);
    });
  }

  /**
   * addTrams
   * Helper method that adds trams to the container and causes them to fly around randomly.
   * @param {Number} numTrams - How many trams to add to the stage
   * @param {Number} speed - Value from 0 (slow) to 10 (fast) that determines how fast the trams will fly
   */
  addTrams(numTrams, speed) {
    for (let i = 0; i < numTrams; i++) {
      const tramColor = 'green';

      // Al was here.
      const newTram = new Tram({
        spritesheet: this.spritesheet,
        colorProfile: tramColor,
        maxX: MAX_X,
        maxY: MAX_Y
      });
      newTram.position.set(TRAM_POINTS.ORIGIN.x, TRAM_POINTS.ORIGIN.y);
      this.addChildAt(newTram, 0);
      newTram.timeline.delay(i);
      newTram.randomTrack({
        speed
      });

      this.trams.push(newTram);
    }
  }

  /**
   * shotsFired
   * Click handler for the stage, scale's the location of the click to ensure coordinate system
   * alignment and then calculates if any of the trams were hit and should be shot.
   * @param {{x:Number, y:Number}} clickPoint - Point where the container was clicked in real coordinates
   * @returns {Number} - The number of trams hit with the shot
   */
  shotsFired(clickPoint) {
    // flash the screen
    this.flashScreen.visible = true;
    _delay(() => {
      this.flashScreen.visible = false;
    }, FLASH_MS);

    let tramsShot = 0;
    for (let i = 0; i < this.trams.length; i++) {
      const tram = this.trams[i];
      if (tram.alive && Utils.pointDistance(tram.position, this.getScaledClickLocation(clickPoint)) < 60) {
        tramsShot++;
        tram.shot();
        tram.timeline.add(() => {
          this.bus.retrieve();
        });
        break;
      }
    }
    return tramsShot;
  }

  clickedReplay(clickPoint) {
    return Utils.pointDistance(this.getScaledClickLocation(clickPoint), HUD_LOCATIONS.REPLAY_BUTTON) < 100;
  }

  clickedMute(clickPoint) {
    return Utils.pointDistance(this.getScaledClickLocation(clickPoint), HUD_LOCATIONS.MUTE_BUTTON) < 50;
  }

  clickedPost(clickPoint) {
    return Utils.pointDistance(this.getScaledClickLocation(clickPoint), HUD_LOCATIONS.POST_BUTTON) < 50;
  }

  clickedShare(clickPoint) {
    return Utils.pointDistance(this.getScaledClickLocation(clickPoint), HUD_LOCATIONS.SHARE_BUTTON) < 50;
  }

  clickedPlay(clickPoint) {

    var point1 = new Point(HUD_LOCATIONS.PLAY_BUTTON.x - 121, HUD_LOCATIONS.PLAY_BUTTON.y - 25);
    var point2 = new Point(HUD_LOCATIONS.PLAY_BUTTON.x + 121, HUD_LOCATIONS.PLAY_BUTTON.y + 25);
    var test =  Utils.inField(this.getScaledClickLocation(clickPoint), point1, point2);
    if (test) {
      this.flashScreen.visible = true;
      _delay(() => {
        this.flashScreen.visible = false;
      }, FLASH_MS);
    }
    return test;
  }

  clickedRanking(clickPoint) {

    var point1 = new Point(HUD_LOCATIONS.RANKING_BUTTON.x - 138, HUD_LOCATIONS.RANKING_BUTTON.y - 19);
    var point2 = new Point(HUD_LOCATIONS.RANKING_BUTTON.x + 138, HUD_LOCATIONS.RANKING_BUTTON.y + 19);
    var test =  Utils.inField(this.getScaledClickLocation(clickPoint), point1, point2);
    if (test) {
      this.flashScreen.visible = true;
      _delay(() => {
        this.flashScreen.visible = false;
      }, FLASH_MS);
    }
    return test;
  }

  getScaledClickLocation(clickPoint) {
    return {
      x: clickPoint.x / this.scale.x,
      y: clickPoint.y / this.scale.y
    };
  }
  /**
   * flyAway
   * Helper method that causes the sky to change color and the trams to fly away
   * @returns {Promise} - This promise is resolved when all the tramss have flown away
   */
  flyAway() {
    this.bus.sad();

    const tramPromises = [];

    for (let i = 0; i < this.trams.length; i++) {
      const tram = this.trams[i];
      if (tram.alive) {
        tramPromises.push(new BPromise((resolve) => {
          tram.stopAndClearTimeline();
          tram.rotation = 0;
          tram.routeTo({
            point: new Point(2000, tram.position.y),
            onComplete: resolve
          });
        }));
      }
    }

    return BPromise.all(tramPromises).then(this.cleanUpTrams.bind(this));
  }

  /**
   * cleanUpTrams
   * Helper that removes all tramss from the container and object
   */
  cleanUpTrams() {
    for (let i = 0; i < this.trams.length; i++) {
      this.removeChild(this.trams[i]);
    }
    this.trams = [];
  }

  /**
   * tramsAlive
   * Helper that returns a boolean value depending on whether or not trams are alive. The distinction
   * is that even dead trams may be animating and still "active"
   * @returns {Boolean}
   */
   tramsAlive() {
    return _some(this.trams, (tram) => {
      return tram.alive;
    });
  }

  /**
   * tramsActive
   * Helper that returns a boolean value depending on whether or not trams are animating. Both live
   * and dead trams may be animating.
   * @returns {Boolean}
   */
  tramsActive() {
    return _some(this.trams, (tram) => {
      return tram.isActive();
    });
  }

  /**
   * busActive
   * Helper proxy method that returns a boolean depending on whether the bus is animating
   * @returns {boolean}
   */
  busActive() {
    return this.bus.isActive();
  }

  /**
   * isActive
   * High level helper to determine if things are animating on the stage
   * @returns {boolean|Boolean}
   */
  isActive() {
    return this.busActive() || this.tramsAlive() || this.tramsActive();
  }
}

export default Stage;



// WEBPACK FOOTER //
// ./src/modules/Stage.js